// src/services/api.js — MPA Advanced v7
// Single source of truth for the API base URL.
export const BASE_URL = process.env.REACT_APP_API_URL || "https://vessel-backend.onrender.com/api";
const BASE = BASE_URL;

// ── REQUEST DEDUPLICATION + BROWSER CACHE ────────────────────────
const inFlight  = new Map();
const respCache = new Map();
const CACHE_TTL = { vessels: 58_000, stats: 120_000, contacts: 5 * 60_000, default: 55_000 };

export function __clearCache() { inFlight.clear(); respCache.clear(); }

function cacheTTL(url) {
  if (url.includes("/vessels"))  return CACHE_TTL.vessels;
  if (url.includes("/stats"))    return CACHE_TTL.stats;
  if (url.includes("/contacts") || url.includes("/vessel-contact")) return CACHE_TTL.contacts;
  return CACHE_TTL.default;
}

// Timeout constants (ms)
// Raised to account for serial Gemini queue wait time at free tier.
// With 3 concurrent features each waiting 5s per slot, a queued request
// can wait 15–30s before even reaching the API. 140s covers the worst case.
const TIMEOUT_DEFAULT  = 30_000;
const TIMEOUT_CONTACTS = 140_000;  // enrichment pipeline: queue wait + API call
const TIMEOUT_AI       = 140_000;  // draft-email, fleet-insights, chat

function timeoutForPath(path) {
  if (path.includes("/vessel-contact") || path.includes("/contacts") ||
      path.includes("/ai-contact"))      return TIMEOUT_CONTACTS;
  if (path.includes("/ai/") || path.includes("/gemini/")) return TIMEOUT_AI;
  return TIMEOUT_DEFAULT;
}

async function call(path, { bustCache = false, method = "GET", body } = {}) {
  const url = `${BASE}${path}`;

  if (bustCache) respCache.delete(url);
  if (method === "GET" && inFlight.has(url)) return inFlight.get(url);

  const cached = respCache.get(url);
  const isTest  = typeof process !== "undefined" && process.env.NODE_ENV === "test";
  if (method === "GET" && !isTest && !bustCache && cached && Date.now() - cached.ts < cacheTTL(url)) {
    return cached.data;
  }

  const promise = (async () => {
    const ctrl    = new AbortController();
    const timer   = setTimeout(() => ctrl.abort(), timeoutForPath(path));
    try {
      const token   = getCurrentUser()?.token;
      const headers = {
        Accept:            "application/json",
        "Accept-Encoding": "gzip, deflate, br",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(cached?.etag ? { "If-None-Match": cached.etag } : {}),
        ...(body ? { "Content-Type": "application/json" } : {}),
      };
      const res = await fetch(url, {
        method,
        headers,
        signal: ctrl.signal,
        ...(body ? { body: typeof body === "string" ? body : JSON.stringify(body) } : {}),
      });

      if (res.status === 304 && cached) {
        respCache.set(url, { ...cached, ts: Date.now() });
        return cached.data;
      }

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch (_) {}
        throw new Error(msg);
      }

      const json = await res.json();
      // For contact endpoints, return the full json (has success + data)
      if (path.includes("/contacts") || path.includes("/vessel-contact")) {
        if (json?.success === false) throw new Error(json.error || "API error");
        const data = json?.data !== undefined ? json.data : json;
        if (method === "GET") respCache.set(url, { data, ts: Date.now(), etag: res.headers.get("etag") || null });
        return data;
      }
      // Gemini endpoints: return full response (not wrapped in .data)
      if (path.includes("/gemini/")) {
        if (json?.success === false) throw new Error(json.error || "Gemini API error");
        return json;
      }

      if (json?.success === false) throw new Error(json.error || "API error");
      const data = json?.data !== undefined ? json.data : json;
      if (method === "GET") respCache.set(url, { data, ts: Date.now(), etag: res.headers.get("etag") || null });
      return data;
    } catch (err) {
      if (err.name === "AbortError")
        throw new Error("Request timed out — the AI is busy, please try again in a moment.");
      if (err.message.includes("Failed to fetch") || err.message.includes("NetworkError"))
        throw new Error("Connecting to server… will retry automatically");
      throw err;
    } finally {
      clearTimeout(timer);
      if (method === "GET") inFlight.delete(url);
    }
  })();

  if (method === "GET") inFlight.set(url, promise);
  return promise;
}

// ── KEEP-ALIVE ────────────────────────────────────────────────────
(function startKeepAlive() {
  const PING_URL = `${BASE}/health`.replace("/api/health", "/health");
  let _pingTimer = null;
  function ping() {
    fetch(PING_URL, { method: "GET", cache: "no-store" })
      .then(r => { if (r.ok) console.debug("[keep-alive] backend awake ✓"); })
      .catch(() => {});
  }
  function schedule() {
    if (_pingTimer) clearInterval(_pingTimer);
    setTimeout(ping, 2000);
    _pingTimer = setInterval(ping, 4 * 60 * 1000);
  }
  if (typeof document !== "undefined") {
    schedule();
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") { ping(); schedule(); }
    });
  }
})();

// ── VESSELS ───────────────────────────────────────────────────────
export async function fetchVessels(
  { search = "", vesselType = "", speedMin = null, speedMax = null, limit = 3000 } = {},
  { bustCache = false } = {}
) {
  const p = new URLSearchParams();
  if (search)         p.set("search",    search);
  if (vesselType)     p.set("vesselType", vesselType);
  if (speedMin != null) p.set("speedMin", speedMin);
  if (speedMax != null) p.set("speedMax", speedMax);
  p.set("limit", limit);
  return call(`/vessels?${p}`, { bustCache });
}

export async function fetchVesselDetail(imo) {
  return call(`/vessels/${encodeURIComponent(imo)}`);
}

export async function fetchVesselHistory(imo, hours = 24) {
  return call(`/vessels/${encodeURIComponent(imo)}/history?hours=${hours}`);
}

// ── ARRIVALS & DEPARTURES ─────────────────────────────────────────
export async function fetchArrivals(limit = 2000, days = 30, bustCache = false) {
  return call(`/arrivals?limit=${limit}&days=${days}`, { bustCache });
}
export async function fetchDepartures(limit = 2000, days = 30, bustCache = false) {
  return call(`/departures?limit=${limit}&days=${days}`, { bustCache });
}
export async function fetchPortActivity()         { return call("/port-activity"); }

// ── CONTACT ENRICHMENT ────────────────────────────────────────────
/**
 * Fetch vessel contacts — owner, operator, manager + port agents.
 * Passes port context so backend can do targeted agent lookups.
 *
 * @param {number} imo
 * @param {Object} opts
 * @param {number}  [opts.mmsi]
 * @param {string}  [opts.name]
 * @param {string}  [opts.currentPort]  — LOCODE or port name
 * @param {string}  [opts.nextPort]     — LOCODE or port name
 * @param {string}  [opts.vesselType]   — e.g. "CONTAINER"
 * @param {boolean} [opts.bustCache]    — force fresh fetch
 */
export async function fetchVesselContacts(imo, {
  mmsi, name, currentPort, nextPort, vesselType, bustCache = false,
} = {}) {
  const p = new URLSearchParams();
  // Only include imo when it is a real positive integer — never 0 or null
  const imoSafe = imo && Number.isInteger(Number(imo)) && Number(imo) > 0 ? Number(imo) : null;
  if (imoSafe)     p.set("imo",         String(imoSafe));
  if (mmsi)        p.set("mmsi",        String(mmsi));
  if (name)        p.set("name",        name);
  if (currentPort) p.set("currentPort", currentPort);
  if (nextPort)    p.set("nextPort",    nextPort);
  if (vesselType)  p.set("vesselType",  vesselType);
  if (!imoSafe && !mmsi && !name) return null; // nothing to identify vessel
  // Use spec endpoint instead — richer response, same data
  return call(`/vessel-contact?${p}`, { bustCache });
}

/**
 * Fetch port agents by LOCODE or port name.
 * Checks BQ first, then static DB, then AI.
 */
export async function fetchPortAgents(portCode, vesselType = "", bustCache = false) {
  const p = new URLSearchParams({ port: portCode });
  if (vesselType) p.set("vesselType", vesselType);
  // FIX BUG 4: renamed from /contacts/agents → /contacts/port-agents
  return call(`/contacts/port-agents?${p}`, { bustCache });
}

/**
 * Trigger force re-run of the full enrichment pipeline for an IMO.
 * Returns enriched contact data immediately.
 */
export async function triggerVesselEnrichment(imo, {
  vessel_name, current_port, next_port, vessel_type,
} = {}) {
  return call(`/contacts/enrich/${encodeURIComponent(imo)}`, {
    method: "POST",
    body: { vessel_name, current_port, next_port, vessel_type },
    bustCache: true,
  });
}

/**
 * Spec endpoint: GET /api/vessel-contact?imo=XXXX
 * Returns the standardized format from the requirements doc.
 */
export async function fetchVesselContactSpec(imo, {
  mmsi, name, currentPort, nextPort, vesselType, enrich, bustCache = false,
} = {}) {
  const p = new URLSearchParams();
  if (imo)             p.set("imo",         String(imo));
  if (mmsi)            p.set("mmsi",        String(mmsi));
  if (name)            p.set("name",        name);
  if (currentPort)     p.set("currentPort", currentPort);
  if (nextPort)        p.set("nextPort",    nextPort);
  if (vesselType)      p.set("vesselType",  vesselType);
  if (enrich === false) p.set("enrich",     "false");
  return call(`/vessel-contact?${p}`, { bustCache });
}

/**
 * Fetch enriched company intelligence (domain + emails + phones)
 * Primary: standard pipeline (DuckDuckGo + DNS + website scraping + SMTP)
 * Fallback: Gemini AI enrichment when standard pipeline finds nothing
 */
export async function fetchVesselIntelligence(imo, { owner, manager, operator, ship_manager, address, forceRefresh = false } = {}) {
  const p = new URLSearchParams();
  if (owner)        p.set("owner",        owner);
  if (manager)      p.set("manager",      manager);
  if (operator)     p.set("operator",     operator);
  if (ship_manager) p.set("ship_manager", ship_manager);
  if (address)      p.set("address",      address);
  if (forceRefresh) p.set("forceRefresh", "true");

  // Try standard pipeline first
  const standard = await call(`/vessel/${encodeURIComponent(imo)}/contact?${p}`, { bustCache: forceRefresh }).catch(() => null);
  const hasData = standard?.top_contacts?.length > 0 ||
    standard?.companies?.some(c => c.emails?.length > 0 || c.domain);
  if (hasData) return standard;

  // Fallback: Gemini AI enrichment
  if (owner || manager) {
    try {
      const gemini = await call(`/gemini/enrich`, {
        method: "POST",
        body: JSON.stringify({ imo, owner, manager, operator, ship_manager, forceRefresh }),
        bustCache: forceRefresh,
      });
      if (gemini?.companies?.length) return { ...gemini, gemini_used: true };
    } catch { /* non-fatal */ }
  }
  return standard;
}

/** Check Gemini API key status */
export async function checkGeminiStatus() {
  return call("/gemini/status").catch(() => ({ configured: false }));
}

/** Directly enrich a company by name using Gemini AI */
export async function geminiEnrichCompany(companyName, domain = null) {
  return call("/gemini/company", {
    method: "POST",
    body: JSON.stringify({ company_name: companyName, domain }),
  });
}

/** Find port agents using Gemini AI */
export async function geminiFindPortAgents(portName, vesselType = "") {
  const p = new URLSearchParams({ port: portName });
  if (vesselType) p.set("vesselType", vesselType);
  return call(`/gemini/port-agents?${p}`);
}

/**
 * Fetch low-efficiency vessels heading to Singapore
 */
export async function fetchLowEfficiencyVessels() {
  return call("/vessels/low-efficiency");
}

// ── AI PREDICTION ─────────────────────────────────────────────────
export async function fetchRoutePrediction(imo) {
  return call(`/predict/${encodeURIComponent(imo)}`);
}

// ── FLEET META ────────────────────────────────────────────────────
export async function fetchVesselTypes() { return call("/vessel-types"); }
export async function fetchFleetStats()  { return call("/stats"); }

// ── AUTH ──────────────────────────────────────────────────────────
async function authPost(endpoint, body) {
  let res;
  try {
    res = await fetch(`${BASE}/auth/${endpoint}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
  } catch { throw new Error("Cannot reach server — backend may be sleeping, wait 30s and retry"); }
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) throw new Error(`Server error (HTTP ${res.status})`);
  return res.json();
}

export async function checkEmailExists(email) {
  try {
    const res  = await fetch(`${BASE}/auth/check-email?email=${encodeURIComponent(email)}`);
    const json = await res.json();
    return json.exists === true;
  } catch { return false; }
}

export async function loginUser(email, password) {
  const json = await authPost("login", { email, password });
  if (!json.success) throw new Error(json.error || "Login failed");
  localStorage.setItem("mt_user", JSON.stringify(json.data));
  return json.data;
}

export async function signupUser(name, email, password) {
  const json = await authPost("register", { name, email, password });
  if (!json.success) {
    if (json.error === "already_registered") {
      const e = new Error(json.message || "Email already registered.");
      e.code = "already_registered"; throw e;
    }
    throw new Error(json.error || "Registration failed");
  }
  localStorage.setItem("mt_user", JSON.stringify(json.data));
  return json.data;
}

export function logoutUser()     { localStorage.removeItem("mt_user"); }
export function getCurrentUser() {
  try { return JSON.parse(localStorage.getItem("mt_user")); }
  catch { return null; }
}
// ── PREFERRED SHIPS (BigQuery-backed, per-user) ───────────────────
export async function fetchPreferredShips() {
  return call("/preferred", { bustCache: true });
}

export async function addPreferredShipAPI(vessel) {
  return call("/preferred", {
    method: "POST",
    body: {
      imo_number:  String(vessel.imo_number || ""),
      vessel_name: vessel.vessel_name || vessel.name || "",
      vessel_type: vessel.vessel_type || "",
      flag:        vessel.flag || "",
    },
    bustCache: true,
  });
}

export async function removePreferredShipAPI(imo) {
  return call(`/preferred/${encodeURIComponent(imo)}`, { method: "DELETE", bustCache: true });
}

export async function clearPreferredShipsAPI() {
  return call("/preferred", { method: "DELETE", bustCache: true });
}

// ── WATCHLIST (BigQuery MPA_Watchlist — per user email) ───────────
export async function fetchWatchlist() {
  return call("/watchlist", { bustCache: true });
}

export async function addToWatchlistAPI(vessel) {
  return call("/watchlist", {
    method: "POST",
    body: {
      imo_number:  String(vessel.imo_number || ""),
      vessel_name: vessel.vessel_name || vessel.name || "",
      vessel_type: vessel.vessel_type || "",
      flag:        vessel.flag || "",
      notes:       vessel.notes || "",
    },
    bustCache: true,
  });
}

export async function removeFromWatchlistAPI(imo) {
  return call(`/watchlist/${encodeURIComponent(imo)}`, { method: "DELETE", bustCache: true });
}

export async function clearWatchlistAPI() {
  return call("/watchlist", { method: "DELETE", bustCache: true });
}

// ── FUEL EFFICIENCY ───────────────────────────────────────────────
export async function fetchVesselFuelEfficiency(imo) {
  return call(`/fuel/vessel/${imo}`);
}

export async function fetchFleetFuelEfficiency(limit = 200) {
  return call(`/fuel/fleet?limit=${limit}`);
}
// ── AI / LLM ENDPOINTS ───────────────────────────────────────────────────────

export async function aiChat(message, history = [], vesselData = null, fleetStats = null) {
  return call("/ai/chat", {
    method: "POST",
    body: { message, history, vesselData, fleetStats },
  });
}

export async function aiSummarizeDocument(text, type = "cargo_report") {
  return call("/ai/summarize", { method: "POST", body: { text, type } });
}

export async function aiDraftEmail(params) {
  return call("/ai/draft-email", { method: "POST", body: params });
}

export async function aiAnalyzeFuel(vesselData, routeData = null) {
  return call("/ai/analyze-fuel", { method: "POST", body: { vesselData, routeData } });
}

export async function aiPredictArrival(vesselData, destination, weatherData = null) {
  return call("/ai/predict-arrival", { method: "POST", body: { vesselData, destination, weatherData } });
}

export async function aiFleetInsights(vessels, stats) {
  return call("/ai/fleet-insights", { method: "POST", body: { vessels, stats } });
}

export async function aiStatus() {
  return call("/ai/status");
}

// ── GEMINI ENRICHMENT ENDPOINTS ───────────────────────────────────────────────

export async function geminiEnrich(imo, owner = null, manager = null, forceRefresh = false) {
  return call("/gemini/enrich", { method: "POST", body: { imo, owner, manager, forceRefresh } });
}

export async function geminiCompany(companyName, domain = null) {
  return call("/gemini/company", { method: "POST", body: { company_name: companyName, domain } });
}

export async function geminiPortAgents(portName, vesselType = "", portCode = "") {
  return call(`/gemini/port-agents?port=${encodeURIComponent(portName)}&vesselType=${encodeURIComponent(vesselType)}&portCode=${encodeURIComponent(portCode)}`);
}

export async function geminiVessel(imo) {
  return call(`/gemini/vessel/${imo}`);
}

export async function geminiStatus() {
  return call("/gemini/status");
}

// ── FUEL ENDPOINTS ────────────────────────────────────────────────────────────

export async function getFuelVessel(imo) {
  return call(`/fuel/vessel/${imo}`);
}

export async function getFuelFleet(limit = 200) {
  return call(`/fuel/fleet?limit=${limit}`);
}